import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useIsPhone } from '@/lib/useMediaQuery'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { uiMessages } from '@/i18n/messages/ui'

export type SelectOption = { value: string; label: string; group?: string }

const TONE = {
  light: {
    btn: 'bg-white ring-1 ring-neutral-300 hover:bg-neutral-50 focus:ring-2 focus:ring-brand-500 dark:bg-neutral-950 dark:ring-neutral-700 dark:hover:bg-neutral-900',
    text: 'text-neutral-900 dark:text-neutral-100',
    muted: 'text-neutral-400',
    menu: 'bg-white shadow-xl ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-700',
    item: 'hover:bg-neutral-100 dark:hover:bg-neutral-800',
    active: 'text-brand-700 dark:text-brand-300',
    search: 'bg-white ring-1 ring-neutral-300 focus:ring-2 focus:ring-brand-500 dark:bg-neutral-950 dark:ring-neutral-700',
    sheet: 'bg-white text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100',
  },
  dark: {
    btn: 'border border-neutral-700 bg-neutral-800 text-neutral-100 hover:bg-neutral-700 focus:border-brand-500',
    text: 'text-neutral-100',
    muted: 'text-neutral-500',
    menu: 'border border-neutral-700 bg-neutral-900 shadow-xl',
    item: 'hover:bg-neutral-800',
    active: 'text-brand-400',
    search: 'border border-neutral-700 bg-neutral-800 text-neutral-100 focus:border-brand-500',
    sheet: 'bg-neutral-900 text-neutral-100',
  },
} as const

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className={`size-4 shrink-0 opacity-50 transition ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="m4 6 4 4 4-4" />
    </svg>
  )
}

/**
 * Searchable custom dropdown. Native <select> stretches the chevron to the far right of a
 * full-width field and is clumsy on a phone; this keeps the arrow next to the label, filters
 * as you type, and opens a bottom sheet below `lg`.
 */
export function Select({
  value, onChange, options, placeholder, empty, disabled, tone = 'light', className, id, onQuery,
  triggerLabel, prefix, size = 'md', triggerClassName,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  empty?: SelectOption
  disabled?: boolean
  tone?: 'light' | 'dark'
  className?: string
  id?: string
  onQuery?: (q: string) => void
  triggerLabel?: string
  prefix?: ReactNode
  size?: 'sm' | 'md'
  triggerClassName?: string
  'aria-label'?: string
}) {
  const phone = useIsPhone()
  const tc = useT(commonMessages)
  const tu = useT(uiMessages)
  const th = TONE[tone]
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const btn = useRef<HTMLButtonElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxH: number } | null>(null)

  const all = empty ? [empty, ...options.filter((o) => o.value !== empty.value)] : options
  const selected = all.find((o) => o.value === value)
  const needle = q.trim().toLowerCase()
  const filtered = onQuery
    ? all
    : needle
      ? all.filter((o) =>
          o.label.toLowerCase().includes(needle)
          || o.value.toLowerCase().includes(needle)
          || (o.group?.toLowerCase().includes(needle) ?? false))
      : all
  const shown = filtered.slice(0, 200)
  const shownLabel = triggerLabel ?? (selected?.label || placeholder || tc('none'))

  const close = () => { setOpen(false); setQ(''); onQuery?.('') }
  const pick = (v: string) => { onChange(v); close() }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  useLayoutEffect(() => {
    if (!open || phone) return
    const place = () => {
      const r = btn.current?.getBoundingClientRect()
      if (!r) return
      const gap = 4
      const below = window.innerHeight - r.bottom - gap - 12
      const above = r.top - gap - 12
      const flip = below < 180 && above > below
      const maxH = Math.max(160, Math.min(320, flip ? above : below))
      setPos({
        top: flip ? r.top - gap - maxH : r.bottom + gap,
        left: Math.min(r.left, window.innerWidth - Math.max(r.width, 220) - 8),
        width: Math.max(r.width, 220),
        maxH,
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, phone])

  useEffect(() => {
    if (open) search.current?.focus()
  }, [open])

  const list: ReactNode = (
    <>
      <div className="p-2">
        <input
          ref={search}
          value={q}
          onChange={(e) => { setQ(e.target.value); onQuery?.(e.target.value) }}
          placeholder={tc('search')}
          aria-label={tc('search')}
          className={`w-full rounded-lg px-2.5 py-2 text-sm outline-none max-lg:min-h-11 max-lg:text-base ${th.search}`}
        />
      </div>
      <ul id={listId} role="listbox" className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
        {shown.map((o, i) => {
          const active = o.value === value
          const showGroup = Boolean(o.group) && o.group !== shown[i - 1]?.group
          return (
            <li key={o.value ? o.value : `__empty-${i}`}>
              {showGroup && (
                <div className={`px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide ${th.muted}`}>{o.group}</div>
              )}
              <button
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => pick(o.value)}
                className={`flex w-full items-center px-3 py-2.5 text-left text-sm max-lg:min-h-11 max-lg:text-base ${th.item} ${active ? th.active : th.text}`}
              >
                <span className="min-w-0 truncate">{o.label || tu('unnamed')}</span>
              </button>
            </li>
          )
        })}
        {shown.length === 0 && (
          <li className={`px-3 py-2.5 text-sm ${th.muted}`}>{tu('noMatches')}</li>
        )}
        {filtered.length > 200 && (
          <li className={`px-3 py-2 text-[11px] ${th.muted}`}>{tu('showingFirst', { max: 200 })}</li>
        )}
      </ul>
    </>
  )

  const menu = !open ? null : phone ? createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col justify-end bg-black/40" onClick={close} role="presentation">
      <div
        role="dialog"
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        className={`flex max-h-[80vh] flex-col rounded-t-2xl shadow-2xl ${th.sheet}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-neutral-300 dark:bg-neutral-600" />
        {list}
      </div>
    </div>,
    document.body,
  ) : pos && createPortal(
    <div className="contents">
      <div className="fixed inset-0 z-[80]" onClick={close} />
      <div
        role="listbox"
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        className={`fixed z-[81] flex flex-col overflow-hidden rounded-xl ${th.menu}`}
        style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxH }}
      >
        {list}
      </div>
    </div>,
    document.body,
  )

  const sizeCls = size === 'sm'
    ? 'min-h-8 px-2 py-1 text-xs max-lg:min-h-11 max-lg:px-2.5 max-lg:text-base'
    : 'min-h-10 px-2.5 py-1.5 text-sm max-lg:min-h-11 max-lg:rounded-xl max-lg:text-base'

  return (
    <div className={`relative ${className ?? 'w-full'}`}>
      <button
        ref={btn}
        type="button"
        {...(id ? { id } : {})}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        {...(open ? { 'aria-controls': listId } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        onClick={() => (open ? close() : setOpen(true))}
        className={`flex w-full items-center justify-start rounded-lg text-left outline-none disabled:opacity-60 ${sizeCls} ${th.btn} ${triggerClassName ?? ''}`}
      >
        <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
          {prefix}
          <span className={`min-w-0 truncate ${selected ? th.text : th.muted}`}>{shownLabel}</span>
          <Chevron open={open} />
        </span>
      </button>
      {menu}
    </div>
  )
}
